import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

/* Small selectable chip */
function ToggleChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full border px-3 py-2 text-sm transition " +
        (active
          ? "bg-redglass-primary text-white shadow-brand"
          : "bg-white hover:bg-white/80 text-redglass-ink")
      }
    >
      {children}
    </button>
  );
}

export default function QuoteModal({ open, onClose, phone = "40744583366" }) {
  const { i18n } = useTranslation("common");
  const lang = (i18n.language || "ro").toLowerCase();

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [open]);

  // Focus first field when open
  const firstFieldRef = useRef(null);
  useEffect(() => {
    if (open) firstFieldRef.current?.focus();
  }, [open]);

  // DEMO selectable topics
  const TOPIC_OPTIONS = useMemo(
    () =>
      lang.startsWith("en")
        ? [
            "Website demo",
            "Design concept",
            "Content placeholder",
            "Component preview",
            "Performance sample",
            "Accessibility sample",
            "Animation sample",
            "Layout variant",
            "Landing mock",
            "Form demo",
            "Other / not sure",
          ]
        : [
            "Demo website",
            "Concept design",
            "Conținut placeholder",
            "Previzualizare componentă",
            "Exemplu performanță",
            "Exemplu accesibilitate",
            "Exemplu animații",
            "Variantă layout",
            "Mock landing",
            "Formular demo",
            "Altceva / nu știu",
          ],
    [lang]
  );

  // Form state (generic demo)
  const [clientName, setClientName] = useState("");
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [deadlineYear, setDeadlineYear] = useState("");
  const [topics, setTopics] = useState([]);
  const [other, setOther] = useState("");
  const [touched, setTouched] = useState(false);

  const toggleTopic = (label) => {
    setTopics((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  const requiredMissing =
    !clientName.trim() ||
    !subject.trim() ||
    !email.trim() ||
    !deadlineYear.toString().trim() ||
    topics.length === 0;

  const buildMessage = () => {
    const lines = lang.startsWith("en")
      ? [
          "Request • Demo Website",
          `Name: ${clientName}`,
          `Email: ${email}`,
          `Subject: ${subject}`,
          `Target year / deadline (approx.): ${deadlineYear}`,
          `Topics: ${topics.join(", ")}`,
          ...(other.trim() ? [`Other details: ${other.trim()}`] : []),
        ]
      : [
          "Cerere • Site Demo",
          `Nume: ${clientName}`,
          `Email: ${email}`,
          `Subiect: ${subject}`,
          `An țintă / termen (aprox.): ${deadlineYear}`,
          `Teme: ${topics.join(", ")}`,
          ...(other.trim() ? [`Alte detalii: ${other.trim()}`] : []),
        ];
    return lines.join("\n");
  };

  const sendWhatsApp = () => {
    const text = encodeURIComponent(buildMessage());
    const url = `https://wa.me/${phone}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onClose?.();
  };

  const sendSMS = async () => {
    const text = encodeURIComponent(buildMessage());
    const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent);
    const sep = isIOS ? "&" : "?";
    const url = `sms:+${phone}${sep}body=${text}`;
    const win = window.open(url, "_blank");
    if (!win) {
      try {
        await navigator.clipboard.writeText(buildMessage());
        alert(
          lang.startsWith("en")
            ? "SMS text copied to clipboard. Paste it in your Messages app."
            : "Textul SMS a fost copiat în clipboard. Lipește-l în aplicația de Mesaje."
        );
      } catch {
        alert(
          lang.startsWith("en")
            ? "Could not open SMS. Please copy the text manually."
            : "Nu am putut deschide SMS. Te rugăm să copiezi textul manual."
        );
      }
    }
    onClose?.();
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (requiredMissing) return;
    sendWhatsApp();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Wrapper */}
      <div className="absolute inset-0 flex items-end sm:items-center justify-center px-0 sm:px-4 py-0 sm:py-8">
        {/* Panel */}
        <div
          className="w-full h-[100svh] sm:h-auto sm:max-w-xl bg-white border shadow-lg rounded-none sm:rounded-2xl flex flex-col"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* Header */}
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b flex items-center justify-between sticky top-0 bg-white">
            <h3
              id="quote-title"
              className="text-base sm:text-lg font-semibold text-redglass-ink"
            >
              {lang.startsWith("en")
                ? "Contact / Demo request"
                : "Contact / Cerere demo"}
            </h3>

            {/* Right-side group: language switcher + close */}
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="scale-90 sm:scale-100 origin-right">
                <LanguageSwitcher />
              </div>

              <button
                onClick={onClose}
                className="rounded-full p-2 hover:bg-white"
                aria-label={lang.startsWith("en") ? "Close" : "Închide"}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <form
            onSubmit={onSubmit}
            className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5 space-y-4"
          >
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {lang.startsWith("en") ? "Name *" : "Nume *"}
              </label>
              <input
                ref={firstFieldRef}
                className="w-full rounded-xl border px-3 py-2 bg-white/80"
                placeholder={
                  lang.startsWith("en") ? "e.g. John Doe" : "ex: Ion Popescu"
                }
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                required
              />
              {touched && !clientName.trim() && (
                <p className="mt-1 text-sm text-red-600">
                  {lang.startsWith("en")
                    ? "This field is required."
                    : "Câmp obligatoriu."}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {lang.startsWith("en") ? "Email *" : "Email *"}
              </label>
              <input
                type="email"
                className="w-full rounded-xl border px-3 py-2 bg-white/80"
                placeholder={
                  lang.startsWith("en")
                    ? "e.g. name@example.com"
                    : "ex: nume@exemplu.com"
                }
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {touched && !email.trim() && (
                <p className="mt-1 text-sm text-red-600">
                  {lang.startsWith("en")
                    ? "This field is required."
                    : "Câmp obligatoriu."}
                </p>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {lang.startsWith("en") ? "Subject *" : "Subiect *"}
              </label>
              <input
                className="w-full rounded-xl border px-3 py-2 bg-white/80"
                placeholder={
                  lang.startsWith("en")
                    ? "e.g. Demo request"
                    : "ex: Cerere demo"
                }
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
              {touched && !subject.trim() && (
                <p className="mt-1 text-sm text-red-600">
                  {lang.startsWith("en")
                    ? "This field is required."
                    : "Câmp obligatoriu."}
                </p>
              )}
            </div>

            {/* Year / Deadline (kept numeric for minimal change in your UI) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {lang.startsWith("en")
                    ? "Target year / deadline *"
                    : "An țintă / termen *"}
                </label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="2020"
                  max={new Date().getFullYear() + 3}
                  className="w-full rounded-xl border px-3 py-2 bg-white/80"
                  placeholder={lang.startsWith("en") ? "e.g. 2026" : "ex: 2026"}
                  value={deadlineYear}
                  onChange={(e) => setDeadlineYear(e.target.value)}
                  required
                />
                {touched && !deadlineYear.toString().trim() && (
                  <p className="mt-1 text-sm text-red-600">
                    {lang.startsWith("en")
                      ? "This field is required."
                      : "Câmp obligatoriu."}
                  </p>
                )}
              </div>
            </div>

            {/* Topics (chips) */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {lang.startsWith("en") ? "Topics / focus *" : "Teme / focus *"}
              </label>
              <div className="flex flex-wrap gap-2">
                {TOPIC_OPTIONS.map((opt) => (
                  <ToggleChip
                    key={opt}
                    active={topics.includes(opt)}
                    onClick={() => toggleTopic(opt)}
                  >
                    {opt}
                  </ToggleChip>
                ))}
              </div>
              {touched && topics.length === 0 && (
                <p className="mt-2 text-sm text-red-600">
                  {lang.startsWith("en")
                    ? "Select at least one option."
                    : "Selectează cel puțin o opțiune."}
                </p>
              )}
            </div>

            {/* Other details */}
            <div>
              <label className="block text-sm font-medium mb-1">
                {lang.startsWith("en")
                  ? "Other details (optional)"
                  : "Alte detalii (opțional)"}
              </label>
              <textarea
                className="w-full rounded-xl border px-3 py-2 min-h-[100px] bg-white/80"
                placeholder={
                  lang.startsWith("en")
                    ? "Describe the demo you want to showcase, pages, components, or notes."
                    : "Descrie demo-ul dorit: pagini, componente sau note."
                }
                value={other}
                onChange={(e) => setOther(e.target.value)}
              />
            </div>

            {/* Footer actions */}
            <div className="pt-2 flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch sm:items-center justify-between">
              <p className="text-xs text-gray-500">
                {lang.startsWith("en")
                  ? "Fields marked with * are required."
                  : "Câmpurile marcate cu * sunt obligatorii."}
              </p>

              <div className="flex gap-2">
                {/* SMS (secondary) */}
                <button
                  type="button"
                  onClick={() => {
                    setTouched(true);
                    if (!requiredMissing) sendSMS();
                  }}
                  disabled={requiredMissing}
                  aria-disabled={requiredMissing}
                  className={
                    "group inline-flex items-center gap-2 rounded-full px-4 py-2.5 border transition " +
                    (requiredMissing
                      ? "cursor-not-allowed border-gray-200 text-gray-400 bg-white"
                      : "border-white/70 bg-white/90 text-redglass-primary hover:bg-white")
                  }
                  title={
                    requiredMissing
                      ? lang.startsWith("en")
                        ? "Fill in the required fields"
                        : "Completează câmpurile obligatorii"
                      : ""
                  }
                >
                  {/* SMS Icon */}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8z" />
                  </svg>
                  <span className="font-medium">
                    {lang.startsWith("en") ? "Send SMS" : "Trimite SMS"}
                  </span>
                </button>

                {/* WhatsApp (primary) */}
                <button
                  type="submit"
                  disabled={requiredMissing}
                  aria-disabled={requiredMissing}
                  className={
                    "group inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-medium text-white shadow-brand ring-1 ring-redglass-ring/40 transition " +
                    (requiredMissing
                      ? "bg-green-400/60 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-600 hover:to-green-800 active:translate-y-px")
                  }
                  title={
                    requiredMissing
                      ? lang.startsWith("en")
                        ? "Fill in the required fields"
                        : "Completează câmpurile obligatorii"
                      : ""
                  }
                >
                  {/* WhatsApp Icon */}
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M20.52 3.48A11.94 11.94 0 0 0 12.06 0C5.49 0 .19 5.3.19 11.87c0 2.09.54 4.15 1.57 5.96L0 24l6.35-1.66a11.87 11.87 0 0 0 5.71 1.45h.01c6.57 0 11.87-5.3 11.87-11.87 0-3.17-1.23-6.16-3.42-8.44Zm-8.46 19.1a9.86 9.86 0 0 1-5.02-1.38l-.36-.21-3.77.98 1.01-3.68-.23-.38A9.86 9.86 0 1 1 12.06 22.58Zm5.69-7.42c-.31-.16-1.81-.89-2.09-.99-.28-.1-.48-.15-.68.16-.2.31-.78.99-.96 1.19-.18.2-.35.22-.66.06-.31-.16-1.3-.48-2.47-1.53-.91-.81-1.53-1.82-1.71-2.13-.18-.31-.02-.47.14-.63.14-.14.31-.35.46-.53.15-.18.2-.31.3-.52.1-.21.05-.39-.03-.55-.08-.16-.68-1.64-.93-2.25-.24-.58-.49-.5-.68-.51l-.58-.01c-.2 0-.52.08-.8.39s-1.05 1.03-1.05 2.51c0 1.48 1.07 2.91 1.22 3.11.15.2 2.09 3.19 5.06 4.47.71.31 1.27.5 1.71.64.72.23 1.38.2 1.9.12.58-.09 1.81-.74 2.07-1.46.26-.72.26-1.33.18-1.46-.07-.12-.28-.2-.6-.36Z" />
                  </svg>
                  <span>
                    {lang.startsWith("en")
                      ? "Send via WhatsApp"
                      : "Trimite pe WhatsApp"}
                  </span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
